import { type Ref } from 'react';
import { Modal, Table } from '@/ui/components/base';
import './gameRules.css';

interface GameRulesProps {
    onBack: () => void;
    ref?: Ref<HTMLDivElement>;
}

export const GameRules = ({ ref, onBack }: GameRulesProps) => {
    return (
        <Modal.Content className="game-rules" ref={ref}>
            <Modal.Header onBack={onBack}>Game Rules</Modal.Header>
            <Modal.Body>
                <div className="game-rules__content">
                    <p className="p">
                        Welcome to Goosicorn Racing! Six runners, one race, and odds that move every
                        round. Pick your runner, place your bet, and see who crosses the line first.
                    </p>

                    <h2 className="h2">How a race works</h2>
                    <p className="p">Each round of Goosicorn Racing runs in three phases:</p>

                    <h3 className="h3">Bet selection</h3>
                    <p className="p">
                        Six runners are drawn from the Goosicorn Racing roster and presented to the
                        player along with their odds for that race. The player has a limited time,
                        shown by the progress bar, to:
                    </p>
                    <ul className="ul">
                        <li className="li">Set a bet amount</li>
                        <li className="li">Tap a runner to view their card</li>
                        <li className="li">Tap Place bet to lock it in their runner</li>
                    </ul>
                    <p className="p">
                        The &quot;Possible win&quot; shown beside the Place bet button is the bet
                        amount multiplied by the selected runner&apos;s odds. Each runner&apos;s
                        card also shows the number of other players who have backed that runner for
                        this race.
                    </p>

                    <h3 className="h3">Round closed and the race</h3>
                    <p className="p">
                        Once the bet selection window ends, the round closes and no further bets can
                        be placed. The runners line up at the start, then sprint along the track.
                        The current standings update live as the race progresses.
                    </p>

                    <h3 className="h3">Result</h3>
                    <p className="p">
                        The winning runner is announced at the finish line. There is only ever one
                        winner per race. If the player&apos;s selected runner finished first,
                        winnings are paid at the odds locked in at the moment of bet placement.
                    </p>

                    <h2 className="h2">Runners and odds</h2>
                    <p className="p">
                        Goosicorn Racing features a roster of runners. Six are drawn for each race.
                        Odds are recalculated for each race based on the lineup. Long shots can pay
                        over 30x; favourites typically pay around 3x to 5x.
                    </p>

                    <h3 className="h3">Return to Player (RTP)</h3>
                    <p className="p">
                        The theoretical Return to Player (RTP) of this game is{' '}
                        <strong>94.00%</strong>, calculated over an extended number of plays.
                    </p>

                    <h3 className="h3">How stake size affects RTP</h3>
                    <p className="p">
                        Winnings are calculated to the exact penny and any fraction smaller than
                        £0.01 is rounded down.
                    </p>
                    <p className="p">
                        At stakes of £1.00 and above, this rounding has no material effect and the
                        full 94.00% RTP applies.
                    </p>
                    <p className="p">
                        At lower stakes, the rounding is more noticeable relative to the bet size,
                        which slightly reduces the effective RTP:
                    </p>

                    <Table className="game-rules__table">
                        <Table.Header>
                            <Table.Row>
                                <Table.Head>Stake</Table.Head>
                                <Table.Head>
                                    <strong>Effective RTP</strong>
                                </Table.Head>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            <Table.Row>
                                <Table.Cell>£1.00 and above</Table.Cell>
                                <Table.Cell>
                                    <strong>94.00%</strong>
                                </Table.Cell>
                            </Table.Row>
                            <Table.Row>
                                <Table.Cell>£0.50</Table.Cell>
                                <Table.Cell>
                                    <strong>93.80%</strong>
                                </Table.Cell>
                            </Table.Row>
                            <Table.Row>
                                <Table.Cell>£0.20</Table.Cell>
                                <Table.Cell>
                                    <strong>93.50%</strong>
                                </Table.Cell>
                            </Table.Row>
                            <Table.Row>
                                <Table.Cell>£0.10 (minimum stake)</Table.Cell>
                                <Table.Cell>
                                    <strong>93.20%</strong>
                                </Table.Cell>
                            </Table.Row>
                        </Table.Body>
                    </Table>

                    <p className="p">
                        The minimum theoretical RTP for this game is therefore{' '}
                        <strong>93.20%</strong>, applicable only at the £0.10 stake level.
                    </p>

                    <h2 className="h2">Game rules</h2>
                    <ul className="ul">
                        <li className="li">
                            Each race features 6 runners drawn from the Goosicorn Racing roster
                        </li>
                        <li className="li">
                            Each runner is assigned odds before bet selection opens
                        </li>
                        <li className="li">Players place a single bet on one runner per race</li>
                        <li className="li">
                            The bet level is set using the bet selector in the UI bar
                        </li>
                        <li className="li">
                            Winnings are paid as bet amount &times; selected runner&apos;s odds, and
                            only if that runner finishes first
                        </li>
                        <li className="li">
                            Only one runner wins each race; all other bets are losing bets
                        </li>
                        <li className="li">
                            Bets and odds are locked at the moment Place bet is tapped
                        </li>
                        <li className="li">
                            Once the round closes, no further bets can be placed for that race
                        </li>
                    </ul>

                    <h2 className="h2">Max win multiplier</h2>
                    <p className="p">The maximum achievable win multiplier in a race is 94.94x.</p>

                    <h2 className="h2">How to play</h2>
                    <ul className="ul">
                        <li className="li">Tapping the Bet field opens the bet selection menu</li>
                        <li className="li">
                            Tapping the desired bet in the menu sets that bet for the race
                        </li>
                        <li className="li">
                            Tapping a runner card selects that runner as your pick
                        </li>
                        <li className="li">Tapping Place bet locks in your bet</li>
                        <li className="li">
                            Tapping the menu icon (three horizontal lines) opens the menu, giving
                            access to the Game Rules, Bet History, and Leave Game options
                        </li>
                        <li className="li">
                            Tapping the speaker icon toggles the sound on and off
                        </li>
                    </ul>

                    <h2 className="h2">Additional information</h2>
                    <p className="p">Simultaneous or coinciding wins are added and paid.</p>
                    <p className="p">
                        The following may be subject to the terms and conditions of the gaming site:
                    </p>
                    <ul className="ul">
                        <li className="li">the process of managing unfinished game rounds</li>
                        <li className="li">
                            the process of closing inactive game sessions after a time specified by
                            the gaming site
                        </li>
                    </ul>
                    <p className="p">
                        In the event of malfunction of the gaming hardware or software all affected
                        pays and plays are rendered void and all affected bets refunded.
                    </p>

                    <h2 className="h2">Unfinished Games</h2>
                    <p className="p">
                        If your game round is disrupted, all game information and placed bets are
                        stored until you reopen the game. You may continue your unfinished round
                        from the point of interruption. Unresolved bets placed but remaining
                        undecided in incomplete games will become void after 90 days. Malfunction
                        voids all pays and plays.
                    </p>
                </div>
            </Modal.Body>
        </Modal.Content>
    );
};
